import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  GraduationCap,
  BookOpen,
  Users,
  CheckCircle,
  BarChart3,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { useAdminAnalytics } from "@/lib/analytics.hooks";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  ChartTooltip,
  ChartConfig,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminOverview,
});

const trendConfig = {
  count: { label: "التسجيلات", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

const attendanceConfig = {
  present: { label: "حاضر", color: "hsl(var(--success, 142.1 76.2% 36.3%))" },
  late: { label: "متأخر", color: "hsl(var(--warning, 38 92% 50%))" },
  absent: { label: "غائب", color: "hsl(var(--destructive))" },
  excused: { label: "بعذر", color: "hsl(var(--muted-foreground))" },
} satisfies ChartConfig;

const courseConfig = {
  active_enrollments: { label: "الطلاب النشطين", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

function AdminOverview() {
  const { data: analytics, isLoading, isError } = useAdminAnalytics();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-extrabold mb-6">لوحة الإحصائيات</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-3xl" />
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Skeleton className="h-[400px] w-full rounded-3xl" />
          <Skeleton className="h-[400px] w-full rounded-3xl" />
        </div>
      </div>
    );
  }

  if (isError || !analytics) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-2xl font-bold">حدث خطأ في تحميل الإحصائيات</h2>
        <p className="text-muted-foreground mt-2">
          يرجى التأكد من تشغيل التحديثات في قاعدة البيانات.
        </p>
      </div>
    );
  }

  const cards = [
    { icon: Users, label: "الطلاب النشطين", value: analytics.activeStudents },
    { icon: CheckCircle, label: "نسبة الإكمال", value: `${analytics.completionRate}%` },
    { icon: TrendingUp, label: "نسبة البقاء (Retention)", value: `${analytics.retentionRate}%` },
    { icon: BookOpen, label: "نسبة تسليم الواجبات", value: `${analytics.submissionRate}%` },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-extrabold">نظرة عامة</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card, i) => (
          <Card key={i} className="rounded-3xl border-0 shadow-soft">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-primary flex items-center justify-center text-primary-foreground">
                <card.icon className="h-6 w-6" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">{card.label}</div>
                <div className="text-3xl font-black">{card.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Enrollment Trend Chart */}
        <Card className="rounded-3xl border-0 shadow-soft">
          <CardHeader>
            <CardTitle>معدل التسجيل</CardTitle>
            <CardDescription>إحصائيات التسجيلات على مدار 12 شهر</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.enrollmentTrend.length === 0 ? (
              <EmptyChartState />
            ) : (
              <ChartContainer config={trendConfig} className="h-[300px] w-full">
                <AreaChart data={analytics.enrollmentTrend}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(val) => val.split("-")[1] + "/" + val.split("-")[0]}
                  />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="var(--color-count)"
                    fill="var(--color-count)"
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Course Popularity Chart */}
        <Card className="rounded-3xl border-0 shadow-soft">
          <CardHeader>
            <CardTitle>الدورات الأكثر إقبالاً</CardTitle>
            <CardDescription>الطلاب النشطين حالياً في كل دورة</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.coursePopularity.length === 0 ? (
              <EmptyChartState />
            ) : (
              <ChartContainer config={courseConfig} className="h-[300px] w-full">
                <BarChart data={analytics.coursePopularity} layout="vertical" margin={{ left: 50 }}>
                  <CartesianGrid horizontal={false} />
                  <XAxis type="number" tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "currentColor" }}
                    width={100}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="active_enrollments"
                    fill="var(--color-active_enrollments)"
                    radius={4}
                  />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Attendance Distribution Chart */}
        <Card className="rounded-3xl border-0 shadow-soft lg:col-span-2">
          <CardHeader>
            <CardTitle>معدلات الحضور</CardTitle>
            <CardDescription>
              نسبة الحضور والغياب خلال 6 شهور ({analytics.attendanceRate}% حضور إجمالي)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.attendanceDistribution.length === 0 ? (
              <EmptyChartState />
            ) : (
              <ChartContainer config={attendanceConfig} className="h-[350px] w-full">
                <BarChart data={analytics.attendanceDistribution}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => val.split("-")[1] + "/" + val.split("-")[0]}
                  />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar
                    dataKey="present"
                    stackId="a"
                    fill="var(--color-present)"
                    radius={[0, 0, 4, 4]}
                  />
                  <Bar dataKey="late" stackId="a" fill="var(--color-late)" />
                  <Bar dataKey="excused" stackId="a" fill="var(--color-excused)" />
                  <Bar
                    dataKey="absent"
                    stackId="a"
                    fill="var(--color-absent)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyChartState() {
  return (
    <div className="h-[300px] w-full flex flex-col items-center justify-center text-muted-foreground bg-muted/20 rounded-xl border border-dashed">
      <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
      <p>لا توجد بيانات متاحة بعد</p>
    </div>
  );
}
